import { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Trash2, Bot, User, Loader2, ArrowLeft, Sparkles, BookOpen } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { modules, getModuleIcon } from '../data/modules';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getSessionId() {
  let sid = localStorage.getItem('k4_chat_session');
  if (!sid) {
    sid = 'k4_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    localStorage.setItem('k4_chat_session', sid);
  }
  return sid;
}

function formatMessage(text) {
  if (!text) return '';
  let html = text;
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-foreground">$1</strong>');
  html = html.replace(/`(.+?)`/g, '<code class="text-xs bg-accent text-[#42CDD7] px-1.5 py-0.5 rounded font-mono">$1</code>');
  html = html.replace(/^(\d+)\.\s(.+)$/gm, '<div class="flex gap-3 items-start py-1"><span class="w-6 h-6 rounded-full bg-accent text-[#42CDD7] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">$1</span><span>$2</span></div>');
  html = html.replace(/^[-•]\s(.+)$/gm, '<div class="flex gap-2 items-start py-0.5"><span class="w-1.5 h-1.5 rounded-full bg-pink-400 shrink-0 mt-2"></span><span>$1</span></div>');
  html = html.replace(/\n\n/g, '<br/><br/>');
  html = html.replace(/\n/g, '<br/>');
  return html;
}

const SUGGESTED_QUESTIONS = [
  { category: 'Getting Started', questions: ['How do I navigate the K4 menu?', 'What are the universal keyboard shortcuts?', 'How do I set up my workstation?'] },
  { category: 'Reception', questions: ['How do I register a patient?', 'How do I scan a referral form?', 'What are the request priority types?'] },
  { category: 'Reporting', questions: ['How do I create a report template?', 'How to insert input fields into templates?', 'How does voice recognition work in K4?'] },
  { category: 'Troubleshooting', questions: ['A request is missing from my worklist', 'Patient cannot be found in the system', 'Report is not appearing in the queue', 'I cannot edit a report'] },
  { category: 'Accounting', questions: ['How do I create a charge scheme?', 'How does the pricing flow work?', 'How to set up invoice reminders?'] },
  { category: 'Admin', questions: ['How do I create a workflow view?', 'How to publish a workflow view profile?', 'How to configure service conflict rules?'] },
];

export default function ChatPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const sessionId = getSessionId();
  const [searchParams] = useSearchParams();

  // Module context from query param
  const moduleSlug = searchParams.get('module');
  const currentModule = moduleSlug ? modules.find(m => m.id === moduleSlug) : null;
  const CurrentModuleIcon = currentModule ? getModuleIcon(currentModule.icon) : null;

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  useEffect(() => {
    loadHistory();
    inputRef.current?.focus();
  }, []);

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API}/chat/history/${sessionId}`);
      if (res.data.length > 0) {
        setMessages(res.data.map(m => ({ role: m.role, content: m.content })));
      }
    } catch (e) {
      console.error('Failed to load history');
    }
  };

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        message: msg,
        session_id: sessionId,
        module_context: currentModule ? currentModule.title : null
      });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
    }
    setLoading(false);
  };

  const handleSubmit = (e) => { e.preventDefault(); sendMessage(); };

  const clearChat = async () => {
    try {
      await axios.post(`${API}/chat/clear/${sessionId}`);
      setMessages([]);
    } catch (e) { console.error('Failed to clear'); }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="chat-page">
      <Header />

      <div className="flex-1 flex flex-col max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8">
        {/* Chat Header */}
        <div className="flex items-center justify-between py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-muted-foreground hover:text-[#42CDD7] transition-colors" data-testid="chat-back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center">
              <Bot className="w-5 h-5 text-[#42CDD7]" />
            </div>
            <div>
              <h1 className="font-['Outfit'] text-lg font-bold text-foreground" data-testid="chat-page-title">K4 Assist</h1>
              {currentModule ? (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <BookOpen className="w-3 h-3 text-pink-500" />
                  Context: <strong className="text-[#42CDD7]">{currentModule.title}</strong>
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">AI-powered K4 expert &middot; GPT-5.2</p>
              )}
            </div>
          </div>
          <button
            data-testid="chat-page-clear-btn"
            onClick={clearChat}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-muted-foreground hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" /> Clear Chat
          </button>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto py-6 space-y-6" data-testid="chat-page-messages">
          {messages.length === 0 && (
            <div className="py-8">
              <div className="text-center mb-10">
                {currentModule ? (
                  <>
                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${currentModule.color}12` }}>
                      <CurrentModuleIcon className="w-8 h-8" style={{ color: currentModule.color }} strokeWidth={1.5} />
                    </div>
                    <h2 className="font-['Outfit'] text-2xl font-bold text-foreground mb-2">Help with {currentModule.title}</h2>
                    <p className="text-sm text-muted-foreground max-w-md mx-auto">
                      I'm focused on <strong className="text-[#42CDD7]">{currentModule.title}</strong>. Ask me anything about this module — procedures, troubleshooting, or step-by-step guidance.
                    </p>
                    <Link to={`/module/${moduleSlug}`} className="inline-flex items-center gap-1.5 mt-3 text-xs text-[#42CDD7] hover:text-[#42CDD7] transition-colors" data-testid="chat-back-to-module">
                      <ArrowLeft className="w-3 h-3" /> Back to {currentModule.title} guide
                    </Link>
                  </>
                ) : (
                  <>
                    <div className="w-16 h-16 rounded-2xl bg-accent flex items-center justify-center mx-auto mb-4">
                      <Sparkles className="w-8 h-8 text-[#42CDD7]" />
                    </div>
                    <h2 className="font-['Outfit'] text-2xl font-bold text-foreground mb-2">How can I help with K4?</h2>
                    <p className="text-sm text-muted-foreground max-w-md mx-auto">
                      I'm your AI K4 expert. Ask me about any procedure, troubleshoot issues, or follow along step-by-step.
                    </p>
                  </>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {SUGGESTED_QUESTIONS.map((cat) => (
                  <div key={cat.category} className="bg-muted rounded-xl p-4 border border-border">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-[#42CDD7] mb-3">{cat.category}</h3>
                    <div className="space-y-1.5">
                      {cat.questions.map((q, i) => (
                        <button
                          key={i}
                          data-testid={`suggested-q-${cat.category.toLowerCase().replace(/\s/g, '-')}-${i}`}
                          onClick={() => sendMessage(q)}
                          className="block w-full text-left px-3 py-2 text-sm text-foreground/80 hover:bg-background hover:text-[#42CDD7] rounded-lg transition-colors"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`} data-testid={`chat-msg-${i}`}>
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                msg.role === 'user' ? 'bg-slate-200' : 'bg-accent'
              }`}>
                {msg.role === 'user' ? (
                  <User className="w-4 h-4 text-foreground/80" />
                ) : (
                  <Bot className="w-4 h-4 text-[#42CDD7]" />
                )}
              </div>
              <div className={`max-w-[75%] ${msg.role === 'user' ? 'text-right' : ''}`}>
                <span className="text-[10px] font-medium text-muted-foreground mb-1 block">
                  {msg.role === 'user' ? 'You' : 'K4 Assist'}
                </span>
                <div className={`px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-[#42CDD7] text-white rounded-tr-md'
                    : 'bg-muted text-foreground rounded-tl-md border border-border'
                }`}>
                  {msg.role === 'user' ? (
                    <span>{msg.content}</span>
                  ) : (
                    <div dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }} />
                  )}
                </div>
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex gap-3">
              <div className="w-9 h-9 rounded-xl bg-accent flex items-center justify-center shrink-0">
                <Bot className="w-4 h-4 text-[#42CDD7]" />
              </div>
              <div>
                <span className="text-[10px] font-medium text-muted-foreground mb-1 block">K4 Assist</span>
                <div className="bg-muted rounded-2xl rounded-tl-md px-4 py-3 border border-border">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 text-pink-500 animate-spin" />
                    <span className="text-sm text-muted-foreground">Analyzing your question...</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="py-4 border-t border-border shrink-0">
          <form onSubmit={handleSubmit} className="flex gap-3">
            <input
              ref={inputRef}
              data-testid="chat-page-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={currentModule ? `Ask about ${currentModule.title}...` : "Ask about any K4 procedure, troubleshoot an issue..."}
              disabled={loading}
              className="flex-1 px-5 py-3.5 text-sm bg-muted border border-border rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#42CDD7]/30 focus:border-[#42CDD7] disabled:opacity-50 transition-all"
            />
            <button
              type="submit"
              data-testid="chat-page-send-btn"
              disabled={loading || !input.trim()}
              className="px-5 py-3.5 bg-[#42CDD7] hover:bg-[#3BB8C2] disabled:bg-slate-300 text-white rounded-2xl transition-colors disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium"
            >
              <Send className="w-4 h-4" />
              <span className="hidden sm:inline">Send</span>
            </button>
          </form>
          <p className="text-center text-[10px] text-muted-foreground mt-2">K4 Assist is based on official Kestral training manuals. Always verify critical actions.</p>
        </div>
      </div>
    </div>
  );
}
